<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import { outcomeLabelKey, outcomeMeta } from '../outcomes';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const dialogRef = ref(null);
const conversationId = ref(null);
const messageId = ref(null);
const selected = ref(null);
const value = ref('');
const isSubmitting = ref(false);
let resolver = null;

// only outcomes that satisfy the channel's resolve requirement (from capi_status)
const outcomeOptions = ref([]);
const options = computed(() =>
  outcomeOptions.value.map(option => ({
    key: option.key,
    requiresValue: option.requires_value,
    ...outcomeMeta(option.key),
  }))
);
const needsValue = computed(() => !!selected.value?.requiresValue);
const canSubmit = computed(
  () => !!selected.value && (!needsValue.value || !!value.value)
);

// Opens the dialog and resolves with true once an outcome was reported, false if dismissed.
const open = ({
  conversationId: convId,
  messageId: msgId,
  outcomeOptions: opts,
}) => {
  conversationId.value = convId;
  messageId.value = msgId;
  outcomeOptions.value = opts || [];
  selected.value = null;
  value.value = '';
  dialogRef.value?.open();
  return new Promise(resolve => {
    resolver = resolve;
  });
};

const finish = result => {
  if (resolver) resolver(result);
  resolver = null;
};

const onClose = () => finish(false);

const submit = async () => {
  if (!canSubmit.value) return;
  isSubmitting.value = true;
  try {
    const payload = { outcome: selected.value.key };
    if (needsValue.value) {
      payload.value = value.value;
      payload.currency = 'BRL';
    }
    await MessageApi.reportCtwaConversion(
      conversationId.value,
      messageId.value,
      payload
    );
    useAlert(t('WASAPI_CAPI.REPORT_SUCCESS'));
    const done = resolver;
    resolver = null;
    dialogRef.value?.close();
    if (done) done(true);
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_CAPI.REPORT_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('WASAPI_CAPI.RESOLVE_BLOCKED_TITLE')"
    :description="t('WASAPI_CAPI.RESOLVE_BLOCKED_DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onClose"
  >
    <div class="flex flex-col gap-3">
      <div class="grid grid-cols-2 gap-2">
        <Button
          v-for="option in options"
          :key="option.key"
          size="sm"
          :variant="selected?.key === option.key ? 'solid' : 'outline'"
          :color="option.color"
          :icon="option.icon"
          :label="t(outcomeLabelKey(option.key))"
          class="w-full"
          @click="selected = option"
        />
      </div>
      <Input
        v-if="needsValue"
        v-model="value"
        type="number"
        min="0"
        step="0.01"
        :label="t('WASAPI_CAPI.VALUE_LABEL')"
        :placeholder="t('WASAPI_CAPI.VALUE_PLACEHOLDER')"
      />
    </div>
    <template #footer>
      <div class="flex items-center justify-end w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="dialogRef.close()"
        />
        <Button
          :label="t('WASAPI_CAPI.RESOLVE_AFTER_REPORT')"
          color="blue"
          :disabled="!canSubmit || isSubmitting"
          :is-loading="isSubmitting"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
